import {FieldPath} from 'react-hook-form'

import {FieldNames, ComplexFieldNames, FieldsForm} from '../types'

/**
 * Normalizes a FieldNames value to the object format {field, fieldPart}.
 * Handles both string and object inputs.
 *
 * @param fieldName - Either a string field path or an object with field/fieldPart
 * @returns Object with field and optional fieldPart
 *
 * @example
 * normalizeFieldName('quantity.volume') // {field: 'quantity.volume'}
 * normalizeFieldName({field: 'dateTime', fieldPart: 'date'}) // {field: 'dateTime', fieldPart: 'date'}
 */
export const normalizeFieldName = (fieldName: FieldNames): ComplexFieldNames => {
  if (typeof fieldName === 'string') {
    return {field: fieldName}
  }
  return fieldName
}

export const findByFieldName = <TItem extends {fieldName: FieldNames}>(
  items: readonly TItem[],
  fieldName: FieldNames,
  fallback: boolean = true
): TItem | undefined => findMatchingField(items, fieldName, fallback, (x) => x.fieldName)

export const findFieldName = (
  fields: readonly FieldNames[],
  fieldName: FieldNames,
  fallback: boolean = true
): FieldNames | undefined => findMatchingField(fields, fieldName, fallback, (x) => x)

// Core matcher for fieldName logic
const findMatchingField = <T>(
  items: readonly T[],
  fieldName: FieldNames,
  fallback: boolean,
  getFieldName: (item: T) => FieldNames
): T | undefined => {
  const normalized = normalizeFieldName(fieldName)

  // First try exact match (field + fieldPart)
  let result = items.find((x) => {
    const fn = normalizeFieldName(getFieldName(x))
    return fn.field === normalized.field && fn.fieldPart === normalized.fieldPart
  })
  // Fallback to field-only match if searching with fieldPart
  if (fallback && !result && normalized.fieldPart) {
    result = items.find((x) => {
      const fn = normalizeFieldName(getFieldName(x))
      return fn.field === normalized.field && !fn.fieldPart
    })
  }

  // Fallback: progressively remove segments after last dot
  // e.g., "a.b.c" -> "a.b" -> "a"
  if (fallback && !result) {
    let currentField = normalized.field
    while (currentField.includes('.')) {
      const lastDotIndex = currentField.lastIndexOf('.')
      currentField = currentField.substring(0, lastDotIndex) as FieldPath<FieldsForm>

      result = items.find((x) => {
        const fn = normalizeFieldName(getFieldName(x))
        return fn.field === currentField && !fn.fieldPart
      })

      if (result) break
    }
  }

  return result
}
